import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';

import {
  BehaviorSubject, Observable, catchError, forkJoin, map, of, skip, switchMap, take
} from 'rxjs';

import { ConfigurationService, IClientAppConfig } from '@sondermind/configuration';
import { BaseHTTPService, HandleErrorOptions } from '@sondermind/http';
import { HTTP_ERROR_HANDLER, IHttpErrorHandler } from '@sondermind/utilities/models-http';

import { Attestation, FINANCIAL_AGREEMENT_DOCUMENT_SLUG } from '../models/attestation.interface';

@Injectable({
  providedIn: 'root'
})
export class AttestationDataService extends BaseHTTPService {
  constructor(
    private configurationService: ConfigurationService<IClientAppConfig>,
    private http: HttpClient,
    @Optional()
    @Inject(HTTP_ERROR_HANDLER)
    httpErr: IHttpErrorHandler[],
  ) {
    super(httpErr);
  }

  private store = new BehaviorSubject<Attestation[]>([]);
  store$ = this.store.asObservable();

  refresh(): Observable<Attestation[]> {
    this.fetch().subscribe((as) => this.store.next(as));
    return this.store$.pipe(skip(1), take(1));
  }

  fetch(opts: Partial<HandleErrorOptions> = {}): Observable<Attestation[]> {
    const url = `${this.configurationService.env.apiBase}/v2/attestations`;
    return this.http
      .get<{ attestations: Attestation[]; }>(url)
      .pipe(
        map((p) => p.attestations),
        catchError<Attestation[], Observable<Attestation[]>>(this.handleError.bind(this, opts)),
      );
  }

  markAgreed(
    id: number | string,
    accountSetupContext: string = null,
    opts: Partial<HandleErrorOptions> = {}
  ): Observable<void> {
    const url = `${this.configurationService.env.apiBase}/v2/attestations/${id}`;
    const body = { attestation: { agreed: true }, account_setup_context: accountSetupContext };

    return this.http
      .patch(url, body)
      .pipe(
        map(() => undefined),
        catchError<undefined, Observable<undefined>>(this.handleError.bind(this, opts))
      );
  }

  /**
   * Ensures the client has a Financial Responsibility Agreement attestation, creating an unsigned one
   * if they have none. Does not sign it.
   */
  upsertFinancial$(opts: Partial<HandleErrorOptions> = {}): Observable<Attestation> {
    const url = `${this.configurationService.env.apiBase}/v2/attestations/upsert_financial`;
    return this.http
      .post<{ attestations: Attestation; }>(url, {})
      .pipe(
        map((p) => p.attestations),
        catchError<Attestation, Observable<Attestation>>(this.handleError.bind(this, opts))
      );
  }

  /**
   * Signs every unsigned Financial Responsibility Agreement the client has, creating one first when
   * there are none. `upsertFinancial$` is only reached when nothing is unsigned, so it can never hand
   * back a signed row while an unsigned one is still outstanding. Emits once nothing is left unsigned.
   */
  signFinancialAgreement$(accountSetupContext: string = null): Observable<void> {
    return this.fetch().pipe(
      map((atts: Attestation[] | null) => (atts ?? []).filter(
        (att) => att.documentSlug === FINANCIAL_AGREEMENT_DOCUMENT_SLUG && !att.agreedAt
      )),
      switchMap((unsigned: Attestation[]) => (unsigned.length > 0 ? of(unsigned) : this.upsertFinancial$().pipe(
        map((att: Attestation | null) => {
          if (!att) {
            throw new Error('No Financial Responsibility Agreement attestation to sign');
          }
          return att.agreedAt ? [] : [att];
        })
      ))),
      switchMap((toSign: Attestation[]) => {
        if (toSign.length === 0) {
          return of<void>(undefined);
        }
        const signs: Array<Observable<void>> = toSign.map((att) => this.markAgreed(att.id, accountSetupContext));
        return forkJoin(signs).pipe(map((): void => undefined));
      })
    );
  }
}
